import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DollarSign, ChevronRight,
  ShieldCheck, Scale, Flame,
  Home, Trees, Building2, Wrench, Landmark,
} from "lucide-react";
import { motion } from "framer-motion";
import { getRealm } from "@/components/lunimar/lunimarRealms";
import {
  ScenarioBrief,
  LunimarDecisionCard,
  ChoiceComparisonGrid,
  LumiSignalCard,
} from "@/components/lunimar-kit";

interface Props {
  onComplete: (capital: number, riskStyle: string, market: string) => void;
  processing: boolean;
}

const CAPITALS = [
  { value: 25000, label: "$25K — Lean Start", desc: "Tighter margins. Forces sharp deal analysis from day one.", risk: "Medium" as const, reward: "Medium" as const, immediate: "+$25,000 starting cash" },
  { value: 50000, label: "$50K — Balanced", desc: "Most flexible profile. Room for reserves after closing.", risk: "Low" as const, reward: "Medium" as const, immediate: "+$50,000 starting cash" },
  { value: 75000, label: "$75K — Comfortable", desc: "Reserves headroom. Can absorb a vacancy or repair shock.", risk: "Low" as const, reward: "High" as const, immediate: "+$75,000 starting cash" },
];

const RISK_STYLES = [
  { value: "conservative", label: "Conservative Operator", desc: "Cash flow first. Slow, steady growth.", icon: ShieldCheck, risk: "Low" as const, reward: "Low" as const },
  { value: "balanced", label: "Balanced Strategist", desc: "Mix of cash flow and appreciation.", icon: Scale, risk: "Medium" as const, reward: "Medium" as const },
  { value: "aggressive", label: "Aggressive Builder", desc: "Higher leverage, faster scaling, bigger swings.", icon: Flame, risk: "High" as const, reward: "High" as const },
];

const MARKETS = [
  { value: "stonegate", label: "Stonegate", desc: "Stable demand, safer cash flow. Slower appreciation.", icon: Home, risk: "Low" as const, reward: "Low" as const, tradeoff: "Lower upside ceiling" },
  { value: "west_hollow", label: "West Hollow", desc: "Balanced rents and prices across mid-tier homes.", icon: Trees, risk: "Medium" as const, reward: "Medium" as const },
  { value: "nova_ridge", label: "Nova Ridge", desc: "Strong appreciation in a volatile growth corridor.", icon: Building2, risk: "High" as const, reward: "High" as const, tradeoff: "Cycles hit harder here" },
  { value: "brighton_vale", label: "Brighton Vale", desc: "Cheap entry prices, more deferred maintenance.", icon: Wrench, risk: "Medium" as const, reward: "Medium" as const, tradeoff: "Repair costs cut into yield" },
  { value: "crest_point", label: "Crest Point", desc: "Premium prices, durable appreciation, lower yields.", icon: Landmark, risk: "Low" as const, reward: "High" as const, tradeoff: "High entry capital required" },
];

export default function InvestorSetup({ onComplete, processing }: Props) {
  const realm = getRealm("re_investing");
  const [capital, setCapital] = useState(50000);
  const [riskStyle, setRiskStyle] = useState("balanced");
  const [market, setMarket] = useState("west_hollow");

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      <ScenarioBrief
        realm={realm}
        eyebrow="Skyline Holdings · Onboarding"
        title="Calibrate your investor profile"
        intent="Pick the capital, risk style, and market you want to operate in. Each choice changes which deals look attractive and how much reserve you carry into the first quarter."
      />

      {/* Starting Capital */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-white/65">
          <DollarSign className="h-3 w-3" /> Starting capital
        </div>
        <ChoiceComparisonGrid columns={3}>
          {CAPITALS.map((c) => (
            <LunimarDecisionCard
              key={c.value}
              realm={realm}
              title={c.label}
              description={c.desc}
              immediateEffect={c.immediate}
              risk={c.risk}
              reward={c.reward}
              selected={capital === c.value}
              onSelect={() => setCapital(c.value)}
            />
          ))}
        </ChoiceComparisonGrid>
      </section>

      {/* Risk Style */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-white/65">
          <Scale className="h-3 w-3" /> Investor style
        </div>
        <ChoiceComparisonGrid columns={3}>
          {RISK_STYLES.map((r) => {
            const Icon = r.icon;
            return (
              <LunimarDecisionCard
                key={r.value}
                realm={realm}
                title={r.label}
                description={r.desc}
                icon={<Icon className="h-4 w-4" />}
                risk={r.risk}
                reward={r.reward}
                selected={riskStyle === r.value}
                onSelect={() => setRiskStyle(r.value)}
              />
            );
          })}
        </ChoiceComparisonGrid>
      </section>

      {/* Market */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-white/65">
          <Building2 className="h-3 w-3" /> Operating market
        </div>
        <ChoiceComparisonGrid columns={1}>
          {MARKETS.map((m) => {
            const Icon = m.icon;
            return (
              <LunimarDecisionCard
                key={m.value}
                realm={realm}
                title={m.label}
                description={m.desc}
                tradeoff={m.tradeoff}
                icon={<Icon className="h-4 w-4" />}
                risk={m.risk}
                reward={m.reward}
                selected={market === m.value}
                onSelect={() => setMarket(m.value)}
              />
            );
          })}
        </ChoiceComparisonGrid>
      </section>

      <LumiSignalCard realm={realm} tag="Brief">
        Larger reserves = more shock absorption. Aggressive in a volatile market amplifies both upside and downside — pair carefully.
      </LumiSignalCard>

      <Button
        onClick={() => onComplete(capital, riskStyle, market)}
        disabled={processing}
        size="lg"
        className="h-12 w-full gap-2 rounded-xl text-base"
      >
        {processing ? (
          <span className="flex items-center gap-2">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground" />
            Spinning up portfolio…
          </span>
        ) : (
          <>
            Lock profile · Begin operations
            <ChevronRight className="h-5 w-5" />
          </>
        )}
      </Button>
    </motion.div>
  );
}
